import { IToastProps, Loading } from '@aon/aon-tws-common-ui';
import * as signalR from '@aspnet/signalr';
import _ from 'lodash';
import { User } from 'oidc-client';
import * as React from 'react';
import { AnyAction } from 'redux';
import Constants from '../constants';
import contentConstants from '../contentConstants';
import { HubConnectionName } from '../models/HubConnectionName';
import {
	INotificationDismissAll,
	INotificationInfo,
	INotificationProcessing,
	INotificationProcessingGeneric,
	INotificationSuccess,
	INotificationSuccessGeneric,
	INotificationWarning,
	INotificationWarningCustom,
	INotificationWarningGeneric,
} from './Actions';
import { ActionTypes } from './ActionTypes';

// NOTE: due to the nature of Toast notifications, we never explicitly REMOVE a notification
// from the instances collection. Toasts call a "dismiss()" handler inside the component, which
// unmounts the component from the DOM, so even though the collection will grow with each
// dispatch, it will not re-render components that have already been dismissed. EMO 1-8-19

export interface INotificationState {
	key?: string;
	instance?: IToastProps;
}

export const defaultToast: IToastProps = {
	message: '',
	messageTitle: '',
	type: undefined,
	hideProgressBar: true,
	autoClose: 3000,
	closeButton: false,
	preventDuplicate: true,
};

export const defaultState: INotificationState = {
	key: undefined,
	instance: undefined,
};

export const hubConnections: Map<string, signalR.HubConnection> = new Map<string, signalR.HubConnection>();

export const ActionCreators = {
	ShowInfoNotification: (message: string) => ({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: message }),
	ConnectToSignalR: (user: User, hubConnectionName: HubConnectionName, dispatchType: AnyAction) => {
		return (dispatch: (action: AnyAction) => void) => {
			let hubConnection: signalR.HubConnection | undefined;
			if (hubConnections.has(hubConnectionName)) {
				hubConnection = hubConnections.get(hubConnectionName);
				if (hubConnection !== undefined) {
					hubConnection.stop();
				}
			}

			hubConnection = new signalR.HubConnectionBuilder()
				.withUrl(Constants.SURVEYBUILDER_API_SIGNALR_URL, {
					accessTokenFactory: () => user.access_token,
				} as signalR.IHttpConnectionOptions)
				.build();

			hubConnection.serverTimeoutInMilliseconds = 30 * 60 * 1000;
			hubConnection.keepAliveIntervalInMilliseconds = 1 * 60 * 1000;

			hubConnection.on(hubConnectionName, (message: any) => {
				if (dispatchType.payload !== undefined) {
					dispatchType.payload = message;
				}
				dispatch(dispatchType);
			});

			hubConnection.start();

			hubConnections.set(hubConnectionName, hubConnection);
		};
	},
	DisconnectSignalR: (hubConnectionName: HubConnectionName) => {
		return (dispatch: (action: AnyAction) => void) => {
			let hubConnection: signalR.HubConnection | undefined;
			if (hubConnections.has(hubConnectionName)) {
				hubConnection = hubConnections.get(hubConnectionName);
				if (hubConnection !== undefined) {
					hubConnection.stop().then(() => {
						hubConnections.delete(hubConnectionName);
					});
				}
			}
		};
	},
};

type KnownActions =
	| INotificationSuccess
	| INotificationWarning
	| INotificationInfo
	| INotificationSuccessGeneric
	| INotificationWarningCustom
	| INotificationWarningGeneric
	| INotificationProcessing
	| INotificationProcessingGeneric
	| INotificationDismissAll;

export const reducer = (state: INotificationState = defaultState, action: KnownActions): INotificationState => {
	const key = Date.now().toString();
	switch (action.type) {
		case ActionTypes.TOAST_INFO_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: action.payload,
					messageTitle: '',
					type: 'info',
				},
			};

		case ActionTypes.TOAST_SUCCESS_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: action.payload,
					messageTitle: contentConstants.TOAST_SUCCESS_TITLE,
				},
			};

		case ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: contentConstants.TOAST_SUCCESS_BODY,
					messageTitle: contentConstants.TOAST_SUCCESS_TITLE,
				},
			};
		case ActionTypes.TOAST_WARNING_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: action.payload,
					type: 'warning',
					messageTitle: contentConstants.TOAST_ERROR_GENERIC_TITLE,
				},
			};
		case ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: action.payload.message,
					type: 'warning',
					messageTitle: action.payload.messageTitle,
				},
			};
		case ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: contentConstants.TOAST_ERROR_GENERIC_BODY,
					type: 'warning',
					messageTitle: contentConstants.TOAST_ERROR_GENERIC_TITLE,
				},
			};
		case ActionTypes.TOAST_PROCESSING_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: (
						<div>
							{action.payload}
							<Loading height={'40px'} width={'40px'} color={'black'} />
						</div>
					),
					type: 'processing',
					autoClose: false,
				},
			};
		case ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION:
			return {
				key,
				instance: {
					...defaultToast,
					message: (
						<div>
							{contentConstants.TOAST_PROCESSING_TEXT}
							<Loading height={'40px'} width={'40px'} color={'black'} />
						</div>
					),
					type: 'processing',
					autoClose: false,
				},
			};
		case ActionTypes.TOAST_DISMISS_ALL:
			return { ...defaultState };
	}

	return state;
};
